// Help panel — opened from the toolkit's HELP button and persists
// until the user taps its X close. Two-column controller reference
// (LEFT / RIGHT physical columns) where each column's contents flip
// based on uiState.dominantHand, so the bindings shown always match
// the user's actual config.

import * as THREE from "three";
import type { Handedness, TopMode } from "../interaction/tool";
import {
  TEXT_DIM,
  TEXT_LIGHT,
  glassFont,
  paintGlassCloseButton,
  paintGlassPanel,
  setupPanelTexture,
} from "./glass";

const CANVAS_W = 1280;
const CANVAS_H = 1024;
const PANEL_W = 0.48;
const PANEL_H = 0.38;
const MARGIN = 40;
const CLOSE_SIZE = 100;

export const HELP_BUTTON_IDS = ["close"] as const;
export type HelpButtonId = (typeof HELP_BUTTON_IDS)[number];

const canvas = document.createElement("canvas");
canvas.width = CANVAS_W;
canvas.height = CANVAS_H;
const texture = new THREE.CanvasTexture(canvas);
setupPanelTexture(texture);

const material = new THREE.MeshBasicMaterial({
  map: texture,
  transparent: true,
  side: THREE.DoubleSide,
  depthTest: false,
  depthWrite: false,
});

export const helpPanelMesh = new THREE.Mesh(
  new THREE.PlaneGeometry(PANEL_W, PANEL_H),
  material,
);
helpPanelMesh.renderOrder = 950;
helpPanelMesh.visible = false;

interface ButtonRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function closeRect(): ButtonRect {
  return {
    x: CANVAS_W - MARGIN - CLOSE_SIZE,
    y: MARGIN,
    w: CLOSE_SIZE,
    h: CLOSE_SIZE,
  };
}

export interface HelpPanelState {
  dominantHand: Handedness;
  topMode: TopMode;
  hot: HelpButtonId | null;
}

// One row of the controls reference: label on the left, value on the
// right. Painted inside the column at the given y.
function paintRow(
  ctx: CanvasRenderingContext2D,
  colX: number,
  colW: number,
  y: number,
  label: string,
  value: string,
): void {
  ctx.fillStyle = TEXT_DIM;
  ctx.font = glassFont("500 32px");
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(label, colX, y);
  ctx.fillStyle = TEXT_LIGHT;
  ctx.font = glassFont("600 36px");
  ctx.fillText(value, colX, y + 38);
  // Subtle divider line below the row.
  ctx.strokeStyle = "rgba(255, 255, 255, 0.10)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(colX, y + 92);
  ctx.lineTo(colX + colW, y + 92);
  ctx.stroke();
}

interface ColumnContent {
  title: string;
  rows: { label: string; value: string }[];
}

function dominantColumn(topMode: TopMode): ColumnContent {
  return {
    title: "Dominant",
    rows: [
      {
        label: "Trigger",
        value:
          topMode === "sketch"
            ? "Draw / Erase / Box / Extrude"
            : "Marquee select · Move · Delete",
      },
      { label: "Grip (full)", value: "Tilt laser 45° down" },
      {
        label: "Joystick",
        value: topMode === "sketch" ? "Shape dial" : "Move / Delete dial",
      },
      { label: "South button", value: "Hold for Toolkit" },
      { label: "North button", value: "Hold for Palette" },
    ],
  };
}

function nonDominantColumn(topMode: TopMode): ColumnContent {
  return {
    title: "Non-dominant",
    rows: [
      // No laser on this hand by design. Trigger engages precision
      // aim on the dominant laser (rotation gets dampened so small
      // wrist movements barely move the cursor — Wacom-style). Grip
      // still contributes to two-grip pan/rotate/zoom (see footer),
      // but it doesn't have a per-hand role of its own.
      { label: "Trigger", value: "Hold for precision aim" },
      { label: "Grip (full)", value: "—" },
      {
        label: "Joystick",
        value: topMode === "sketch" ? "Action dial (Draw / Erase)" : "—",
      },
      { label: "South button", value: "Undo" },
      { label: "North button", value: "Redo" },
    ],
  };
}

export function paintHelpPanel(state: HelpPanelState): void {
  const ctx = canvas.getContext("2d")!;
  paintGlassPanel(ctx, CANVAS_W, CANVAS_H);

  // Title.
  ctx.fillStyle = TEXT_LIGHT;
  ctx.font = glassFont("700 66px");
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("Controls", MARGIN + 8, MARGIN + 8);

  // Close button.
  const close = closeRect();
  paintGlassCloseButton(ctx, close.x, close.y, close.w, close.h, state.hot === "close");

  // Two physical columns: LEFT and RIGHT controller. The roles
  // (dominant / non-dominant) swap based on uiState.dominantHand so the
  // reference always matches reality.
  const colGap = 32;
  const colW = (CANVAS_W - 2 * MARGIN - colGap) / 2;
  const colY = 200;

  const leftIsDominant = state.dominantHand === "left";
  const left = leftIsDominant
    ? dominantColumn(state.topMode)
    : nonDominantColumn(state.topMode);
  const right = leftIsDominant
    ? nonDominantColumn(state.topMode)
    : dominantColumn(state.topMode);

  const drawColumn = (
    colX: number,
    handLabel: string,
    content: ColumnContent,
  ) => {
    // Column header: hand + role.
    ctx.fillStyle = TEXT_LIGHT;
    ctx.font = glassFont("700 44px");
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(handLabel, colX, colY);
    ctx.fillStyle = TEXT_DIM;
    ctx.font = glassFont("500 28px");
    ctx.fillText(content.title, colX, colY + 56);
    // Rows.
    const rowH = 108;
    for (let i = 0; i < content.rows.length; i++) {
      const r = content.rows[i];
      paintRow(ctx, colX, colW, colY + 116 + i * rowH, r.label, r.value);
    }
  };

  drawColumn(MARGIN, "Left", left);
  drawColumn(MARGIN + colW + colGap, "Right", right);

  // Footer line for combined gestures that aren't per-hand.
  ctx.fillStyle = TEXT_DIM;
  ctx.font = glassFont("500 28px");
  ctx.textAlign = "center";
  ctx.fillText(
    "Both grips: pan / rotate / scale canvas   ·   Grab bar trigger: drag canvas",
    CANVAS_W / 2,
    CANVAS_H - MARGIN - 30,
  );

  texture.needsUpdate = true;
}

export function pickHelpButton(
  uvX: number,
  uvY: number,
): HelpButtonId | null {
  const x = uvX * CANVAS_W;
  const y = (1 - uvY) * CANVAS_H;
  const close = closeRect();
  if (
    x >= close.x &&
    x <= close.x + close.w &&
    y >= close.y &&
    y <= close.y + close.h
  ) {
    return "close";
  }
  return null;
}
